import type { PERMISSIONS } from "@genius-tracker/api-client/permission-catalog";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { apiGet } from "./api.js";
import { useSession } from "../session/SessionProvider.js";

/** Every catalogued permission (see access/roles.ts for why not role-derived). */
export type Permission = (typeof PERMISSIONS)[number];

interface AccessResponse {
  readonly permissions: readonly Permission[];
}

const ACCESS_QUERY_KEY = ["me", "access"] as const;

/**
 * Reads the server-resolved access set. A missing or failed response is
 * deliberately represented by an empty set: UI gates must fail closed.
 */
export async function fetchAccess(signal?: AbortSignal): Promise<AccessResponse> {
  return apiGet<AccessResponse>("/v1/me/access", undefined, signal);
}

export function useAccess(): {
  roles: readonly string[];
  can: (permission: Permission) => boolean;
} {
  const session = useSession();
  const access = useQuery({
    queryKey: [...ACCESS_QUERY_KEY, session.tenantId, session.identity.sub],
    queryFn: ({ signal }) => fetchAccess(signal),
    staleTime: Infinity,
  });
  const permissions = useMemo(
    () => new Set<Permission>(access.data?.permissions ?? []),
    [access.data],
  );
  return useMemo(
    () => ({
      roles: session.roles,
      can: (permission: Permission) => permissions.has(permission),
    }),
    [session.roles, permissions],
  );
}
